import { useEffect, useState } from "react";
import { toast } from "sonner";
import UploadScenePromptsButton from "./UploadScenePromptsButton";
import JobProgressBanner from "./JobProgressBanner";

/**
 * Per-scene approval grid shown on the AutoVideo page when a job uses
 * the two-step pipeline (Active-Channels launches).
 *
 * Each scene card:
 *   • Visual description (from script.scene_breakdown[i])
 *   • Narration slice (what's spoken during this scene)
 *   • Generated image (placeholder skeleton while pending/generating)
 *   • ✓ Approve · ↻ Regenerate (with optional custom prompt) · ⬆ Upload
 *
 * State shape (from /scenes/approval-state):
 *   { scenes_status: string[], scene_image_urls: string[],
 *     scene_image_provider: string[], scene_image_errors: string[],
 *     narration_slices: string[], scene_descriptions: string[],
 *     all_scenes_approved: boolean }
 */
export default function SceneApprovalList({
  state,
  apiBase,
  onApprove,
  onUnapprove,
  onRegenerate,
  onUpload,
  onAnimateAll,
  onRetryAll,
  animateBusy,
  onAnimateScene,
  onAnimateAllApproved,
  onStopAnimateAll,
  animatingScenes,
  animateAllBusy,
  onStitchFinalVideo,
  stitchBusy,
  onSaveAnimationPrompt,
  onBulkAnimationPromptsFromDoc,
}) {
  if (!state || !Array.isArray(state.scenes_status)) return null;
  const count = state.scenes_status.length;
  if (count === 0) return null;

  const approvedCount = state.scenes_status.filter(
    (s) => s === "approved",
  ).length;
  const readyCount = state.scenes_status.filter(
    (s) => s === "ready" || s === "approved",
  ).length;
  const failedCount = state.scenes_status.filter(
    (s) => s === "failed",
  ).length;
  const generatingCount = state.scenes_status.filter(
    (s) => s === "generating",
  ).length;
  const pendingCount = state.scenes_status.filter(
    (s) => s === "pending",
  ).length;
  const allApproved = state.all_scenes_approved && approvedCount === count;

  const animStatusArr = state.scene_animation_status || [];
  const animUrlsArr = state.scene_animation_urls || [];
  const animErrorsArr = state.scene_animation_errors || [];

  // How many APPROVED scenes still need a Veo3 clip? Drives the
  // "Animate All Approved" button label/enabled state.
  const pendingApprovedAnim = state.scenes_status.reduce((n, s, i) => {
    if (s !== "approved") return n;
    const a = animStatusArr[i] || "";
    if (a === "ready" || a === "animating") return n;
    return n + 1;
  }, 0);
  const animatedCount = animStatusArr.filter((a) => a === "ready").length;

  const allAnimated =
    count > 0 && animStatusArr.length === count
    && animStatusArr.every((a) => a === "ready");
  const stitchedStatus = state.stitched_status || "";
  const stitchedUrl = state.stitched_video_url || "";
  const stitchedError = state.stitched_error || "";
  const stitching = stitchBusy || stitchedStatus === "running";
  const stitchReady = stitchedStatus === "ready" && !!stitchedUrl;
  const stitchFailed = stitchedStatus === "failed";
  const fullStitchedUrl = (() => {
    if (!stitchedUrl) return "";
    if (stitchedUrl.startsWith("http")) return stitchedUrl;
    if (stitchedUrl.startsWith("/")) return `${apiBase}${stitchedUrl}`;
    return stitchedUrl;
  })();

  return (
    <section
      data-testid="scene-approval-list"
      className="space-y-5"
    >
      {(() => {
        // Compute the most-relevant stage so the banner colour + label
        // match what's actually happening.
        const animating = animStatusArr.filter((a) => a === "animating").length;
        const animFailed = animStatusArr.filter((a) => a === "failed").length;
        const totalScenes = count;
        const totalReady = animatedCount;
        let stage = "queued";
        if (stitching) stage = "stitching";
        else if (stitchFailed) stage = "failed";
        else if (stitchReady && totalReady === totalScenes) stage = "complete";
        else if (animateAllBusy || animating > 0) stage = "animating";
        else if (allAnimated) stage = "complete";
        else if (generatingCount > 0) stage = "queued";
        return (
          <JobProgressBanner
            stage={stage}
            total={totalScenes}
            ready={totalReady}
            animating={animating}
            queued={Math.max(0, totalScenes - totalReady - animating - animFailed)}
            failed={animFailed}
            jobLabel={
              generatingCount > 0 && stage === "queued"
                ? `Generating scene images · ${readyCount}/${totalScenes} ready`
                : stage === "stitching"
                  ? "Stitching final video — keep this tab open"
                  : ""
            }
            testIdPrefix="scene-approval-progress"
          />
        );
      })()}

      <div className="flex flex-wrap items-end justify-between gap-3 pb-1">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-yellow-400 font-bold">
            // STEP 1 · APPROVE EACH SCENE
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-0.5">
            {readyCount}/{count} ready · {approvedCount}/{count} approved
            {generatingCount > 0 && ` · ${generatingCount} generating`}
            {pendingCount > 0 && ` · ${pendingCount} pending`}
            {failedCount > 0 && ` · ${failedCount} failed`}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {onBulkAnimationPromptsFromDoc && (
            <UploadScenePromptsButton
              testIdPrefix="scene-approval-upload-prompts"
              onPrompts={(parsed) => onBulkAnimationPromptsFromDoc(parsed)}
              label="Upload scene descriptions"
            />
          )}
          {failedCount > 0 && (
            <button
              type="button"
              data-testid="scene-approval-retry-all"
              onClick={onRetryAll}
              className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-300 hover:text-amber-200 border border-amber-400/40 px-3 py-1.5 rounded-sm transition-colors"
            >
              ↻ Retry failed
            </button>
          )}
          {animateAllBusy ? (
            <button
              type="button"
              data-testid="scene-approval-animate-all-approved-stop"
              onClick={onStopAnimateAll}
              className="font-mono text-[10px] uppercase tracking-[0.25em] bg-red-500/20 border border-red-500/60 text-red-300 hover:bg-red-500/30 px-3 py-2 rounded-sm transition-colors"
              title="Stop after the current scene finishes animating"
            >
              ■ Stop animating
            </button>
          ) : (
            <button
              type="button"
              data-testid="scene-approval-animate-all-approved"
              onClick={onAnimateAllApproved}
              disabled={pendingApprovedAnim === 0 || approvedCount === 0}
              className={`font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-2 rounded-sm transition-colors border ${
                pendingApprovedAnim > 0
                  ? "bg-fuchsia-500/15 border-fuchsia-400/60 text-fuchsia-200 hover:bg-fuchsia-500/25"
                  : "bg-zinc-900 border-zinc-800 text-zinc-600 cursor-not-allowed"
              }`}
              title={
                approvedCount === 0
                  ? "Approve scenes first"
                  : pendingApprovedAnim === 0
                    ? "All approved scenes are already animated"
                    : "Send each approved scene to Veo3 sequentially (8s clips)"
              }
            >
              ▶▶ Animate All Approved
              {pendingApprovedAnim > 0
                ? ` (${pendingApprovedAnim})`
                : approvedCount > 0 && animatedCount > 0
                  ? ` · ${animatedCount}/${approvedCount} done`
                  : ""}
            </button>
          )}
          <button
            type="button"
            data-testid="scene-approval-animate"
            onClick={onAnimateAll}
            disabled={!allApproved || animateBusy}
            className={`font-display text-sm font-black uppercase tracking-wide px-5 py-2.5 rounded-sm transition-colors ${
              allApproved && !animateBusy
                ? "bg-emerald-400 text-black hover:bg-emerald-300"
                : "bg-zinc-800 text-zinc-500 cursor-not-allowed"
            }`}
            title={
              allApproved
                ? "Render the final stitched MP4 (uses approved scenes + voiceover)"
                : `Approve all ${count} scenes first`
            }
          >
            {animateBusy
              ? "▸ Starting animation…"
              : allApproved
                ? `▶ Render full video`
                : `▶ Render full video (${approvedCount}/${count})`}
          </button>
        </div>
      </div>

      <div
        className="grid grid-cols-1 lg:grid-cols-2 gap-4"
        data-testid="scene-approval-grid"
      >
        {state.scenes_status.map((status, idx) => (
          <SceneCard
            key={idx}
            idx={idx}
            status={status}
            description={state.scene_descriptions?.[idx] || ""}
            narration={state.narration_slices?.[idx] || ""}
            imageUrl={state.scene_image_urls?.[idx] || ""}
            provider={state.scene_image_provider?.[idx] || ""}
            errorMsg={state.scene_image_errors?.[idx] || ""}
            animStatus={animStatusArr[idx] || ""}
            animUrl={animUrlsArr[idx] || ""}
            animError={animErrorsArr[idx] || ""}
            animationPrompt={state.scene_animation_prompts?.[idx] || ""}
            isAnimating={
              !!animatingScenes && typeof animatingScenes.has === "function"
                ? animatingScenes.has(idx)
                : false
            }
            animateAllBusy={!!animateAllBusy}
            apiBase={apiBase}
            onApprove={() => onApprove(idx)}
            onUnapprove={() => onUnapprove(idx)}
            onRegenerate={(prompt) => onRegenerate(idx, prompt)}
            onUpload={(file) => onUpload(idx, file)}
            onAnimate={() => onAnimateScene && onAnimateScene(idx)}
            onSaveAnimationPrompt={onSaveAnimationPrompt}
          />
        ))}
      </div>

      {/* Stitch Final Video — concatenates every per-scene Veo3 clip
          (8s each) plus the voiceover into one final MP4. */}
      <div
        data-testid="stitch-final-block"
        className="border border-zinc-800 rounded-sm p-4 bg-black/40 space-y-3"
      >
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-400 font-bold">
              // STEP 3 · STITCH FINAL VIDEO
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-0.5">
              {allAnimated
                ? `${count}/${count} clips ready — concat + voiceover → final MP4`
                : `Waiting on Veo3 clips · ${animatedCount}/${count} animated`}
            </div>
          </div>
          <button
            type="button"
            data-testid="stitch-final-video-btn"
            onClick={onStitchFinalVideo}
            disabled={!allAnimated || stitching}
            className={`font-display text-sm font-black uppercase tracking-wide px-5 py-2.5 rounded-sm transition-colors ${
              allAnimated && !stitching
                ? "bg-cyan-400 text-black hover:bg-cyan-300"
                : "bg-zinc-800 text-zinc-500 cursor-not-allowed"
            }`}
            title={
              !allAnimated
                ? "Animate every scene first (per-scene ▶ Animate or ▶▶ Animate All Approved)"
                : stitchReady
                  ? "Re-stitch the final video"
                  : "Concat all Veo3 clips + voiceover → final MP4"
            }
          >
            {stitching
              ? "▸ Stitching…"
              : stitchReady
                ? "↻ Re-stitch final video"
                : "🎬 Stitch final video"}
          </button>
        </div>

        {stitching && (
          <div
            data-testid="stitch-progress"
            className="flex items-center gap-2 text-[11px] text-zinc-300"
          >
            <div className="w-3 h-3 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" />
            <span>FFmpeg concat + voiceover mux — ~1-2 min per minute of footage…</span>
          </div>
        )}
        {stitchFailed && (
          <div
            data-testid="stitch-error"
            className="text-[11px] text-red-300 leading-relaxed border border-red-500/30 rounded-sm bg-red-500/5 p-2"
          >
            Stitch failed: {stitchedError || "ffmpeg error"}
          </div>
        )}
        {stitchReady && (
          <video
            key={fullStitchedUrl}
            src={fullStitchedUrl}
            controls
            playsInline
            preload="metadata"
            data-testid="stitch-final-video"
            className="w-full aspect-video bg-black rounded-sm border border-cyan-400/40"
          />
        )}
        {stitchReady && (
          <a
            href={fullStitchedUrl}
            download
            data-testid="stitch-final-video-download"
            className="inline-block font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-300 hover:text-cyan-200 border border-cyan-400/40 px-3 py-1.5 rounded-sm transition-colors"
          >
            ⬇ Download final MP4
          </a>
        )}
      </div>
    </section>
  );
}

function SceneCard({
  idx, status, description, narration, imageUrl, provider, errorMsg,
  apiBase, onApprove, onUnapprove, onRegenerate, onUpload,
  animStatus, animUrl, animError, isAnimating, animateAllBusy, onAnimate,
  animationPrompt, onSaveAnimationPrompt,
}) {
  const [showEdit, setShowEdit] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [regenBusy, setRegenBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [approveBusy, setApproveBusy] = useState(false);

  // The "animate prompt" is what gets sent to Veo 3 when the user
  // clicks ▶ Animate on this scene. Defaults to the LLM-generated
  // visual description, but the user can override it inline. The
  // override is persisted via PATCH on blur.
  const [showAnimPrompt, setShowAnimPrompt] = useState(false);
  const [animPromptDraft, setAnimPromptDraft] = useState(
    animationPrompt || description || "",
  );
  const [animPromptSaving, setAnimPromptSaving] = useState(false);
  // Re-sync when the upstream value changes (e.g. switching jobs).
  useEffect(() => {
    setAnimPromptDraft(animationPrompt || description || "");
  }, [animationPrompt, description]);
  const effectiveAnimPrompt = (animationPrompt || description || "").trim();
  const handleSaveAnimPrompt = async (next) => {
    if (!onSaveAnimationPrompt) return;
    setAnimPromptSaving(true);
    try {
      // Empty string clears the override → backend falls back to the
      // LLM description on the next animate.
      const toSend = (next || "").trim() === description.trim() ? "" : next;
      await onSaveAnimationPrompt(idx, toSend);
    } catch (err) {
      console.debug("animate prompt save failed:", err?.message);
    } finally {
      setAnimPromptSaving(false);
    }
  };

  const fullUrl = (() => {
    if (!imageUrl) return "";
    if (imageUrl.startsWith("http")) return imageUrl;
    if (imageUrl.startsWith("/")) return `${apiBase}${imageUrl}`;
    return imageUrl;
  })();

  const fullAnimUrl = (() => {
    if (!animUrl) return "";
    if (animUrl.startsWith("http")) return animUrl;
    if (animUrl.startsWith("/")) return `${apiBase}${animUrl}`;
    return animUrl;
  })();

  // Local UI flag — true while the parent has dispatched the animate
  // request OR the backend's per-scene status is "animating".
  const animatingNow = isAnimating || animStatus === "animating";
  const animReady = animStatus === "ready" && !!fullAnimUrl;
  const animFailed = animStatus === "failed";

  // Animate is ONLY enabled once the scene is APPROVED. A "ready"
  // (generated but not yet approved) image is intentionally locked out
  // to force the user to review/approve before burning Veo3 credits.
  const isApproved = status === "approved";
  const animateEnabled = isApproved && !animatingNow && !animateAllBusy;

  // Reason the button is disabled — drives the tooltip + label.
  const animateDisabledReason = (() => {
    if (animatingNow) return "Veo 3 is animating this scene…";
    if (animateAllBusy) return "Batch animation running — wait for it to finish";
    if (status === "pending" || status === "generating") {
      return "Waiting for scene image to finish generating";
    }
    if (status === "failed") return "Scene image failed — regenerate first";
    if (status === "ready" && !isApproved) {
      return "Approve this scene first (✓ Approve button above)";
    }
    return "";
  })();

  const handleRegen = async () => {
    setRegenBusy(true);
    try {
      await onRegenerate(customPrompt.trim() || "");
      setShowEdit(false);
      setCustomPrompt("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Regenerate failed");
    } finally {
      setRegenBusy(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";  // allow re-selecting same file
    if (!file) return;
    setUploadBusy(true);
    try {
      await onUpload(file);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadBusy(false);
    }
  };

  const handleApproveClick = async () => {
    setApproveBusy(true);
    try {
      if (status === "approved") await onUnapprove();
      else await onApprove();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approve failed");
    } finally {
      setApproveBusy(false);
    }
  };

  return (
    <div
      data-testid={`scene-card-${idx}`}
      data-status={status}
      className={`border rounded-sm overflow-hidden bg-black/40 transition-colors ${
        status === "approved"
          ? "border-emerald-400/60"
          : status === "failed"
            ? "border-red-500/50"
            : status === "ready"
              ? "border-zinc-700"
              : "border-zinc-800"
      }`}
    >
      <div className="aspect-video bg-zinc-950 relative border-b border-zinc-800">
        {status === "ready" || status === "approved" ? (
          fullUrl && (
            <img
              key={fullUrl}
              src={fullUrl}
              alt={`Scene ${idx + 1}`}
              className="w-full h-full object-cover"
              data-testid={`scene-card-${idx}-img`}
            />
          )
        ) : status === "generating" ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
            <div className="w-8 h-8 border-2 border-yellow-400/30 border-t-yellow-400 rounded-full animate-spin" />
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400">
              generating<span className="blink">_</span>
            </div>
          </div>
        ) : status === "failed" ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4 text-center">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-red-400">
              generation failed
            </div>
            <div className="text-[10px] text-zinc-400 max-w-full leading-relaxed line-clamp-3">
              {errorMsg || "All image providers failed."}
            </div>
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              queued
            </div>
          </div>
        )}
        <div className="absolute top-2 left-2 font-mono text-[10px] uppercase tracking-[0.2em] bg-black/80 border border-zinc-800 text-zinc-200 px-2 py-0.5 rounded-sm">
          Scene {idx + 1}
        </div>
        {status === "approved" && (
          <div
            data-testid={`scene-card-${idx}-approved-badge`}
            className="absolute top-2 right-2 font-mono text-[10px] uppercase tracking-[0.2em] bg-emerald-400 text-black px-2 py-0.5 rounded-sm font-bold"
          >
            ✓ approved
          </div>
        )}
        {provider && provider !== "user-upload" && status !== "approved" && (
          <div className="absolute bottom-2 right-2 font-mono text-[9px] uppercase tracking-[0.2em] bg-black/70 border border-zinc-800 text-zinc-300 px-1.5 py-0.5 rounded-sm">
            via {provider.replace("gemini-nano-banana-pro", "nano-banana")
              .replace("openai-gpt-image", "openai")
              .replace("fal-flux", "fal")}
          </div>
        )}
        {provider === "user-upload" && (
          <div className="absolute bottom-2 right-2 font-mono text-[9px] uppercase tracking-[0.2em] bg-cyan-400 text-black px-1.5 py-0.5 rounded-sm font-bold">
            your image
          </div>
        )}
      </div>

      <div className="p-4 space-y-3">
        <div>
          <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
            VISUAL
          </div>
          <div
            className="text-sm text-zinc-100 leading-snug"
            data-testid={`scene-card-${idx}-desc`}
          >
            {description || "(no description)"}
          </div>
        </div>
        {narration && (
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
              DIALOG · WHAT&apos;S SPOKEN
            </div>
            <div
              className="text-xs text-zinc-300 leading-relaxed italic border-l-2 border-zinc-800 pl-3"
              data-testid={`scene-card-${idx}-dialog`}
            >
              &ldquo;{narration}&rdquo;
            </div>
          </div>
        )}

        {/* Animate prompt — what gets sent to Veo 3 when the user
            clicks ▶ Animate. Defaults to the visual description but
            can be tweaked inline. Persisted on the job doc so
            re-animating uses the override. */}
        <div className="border border-zinc-800 rounded-sm bg-black/40 p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-fuchsia-300">
              🎬 ANIMATE PROMPT · sent to veo 3
              {animationPrompt ? (
                <span
                  className="ml-2 text-fuchsia-400"
                  data-testid={`scene-card-${idx}-anim-prompt-custom-badge`}
                  title="You've customized this prompt — auto-description is overridden"
                >· custom</span>
              ) : null}
              {animPromptSaving && (
                <span className="ml-2 text-zinc-500">saving…</span>
              )}
            </div>
            <button
              type="button"
              data-testid={`scene-card-${idx}-anim-prompt-toggle`}
              onClick={() => setShowAnimPrompt((v) => !v)}
              className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-fuchsia-300 transition-colors"
              title={showAnimPrompt ? "Hide" : "Edit the prompt sent to Veo 3"}
            >
              {showAnimPrompt ? "▼ hide" : "✎ edit"}
            </button>
          </div>
          {showAnimPrompt ? (
            <>
              <textarea
                rows={3}
                value={animPromptDraft}
                onChange={(e) => setAnimPromptDraft(
                  e.target.value.slice(0, 2000),
                )}
                onBlur={() => handleSaveAnimPrompt(animPromptDraft)}
                disabled={animatingNow}
                placeholder={description || "What should happen in this 8s clip..."}
                data-testid={`scene-card-${idx}-anim-prompt-input`}
                className="w-full bg-black border border-zinc-800 focus:border-fuchsia-400/60 focus:outline-none rounded-sm px-2.5 py-1.5 text-xs text-zinc-100 leading-relaxed resize-y"
              />
              <div className="flex items-center justify-between gap-2">
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">
                  ▸ saves on blur · clear to fall back to auto-description
                </div>
                {animationPrompt && (
                  <button
                    type="button"
                    data-testid={`scene-card-${idx}-anim-prompt-reset`}
                    onClick={async () => {
                      setAnimPromptDraft(description || "");
                      await handleSaveAnimPrompt("");
                    }}
                    disabled={animatingNow || animPromptSaving}
                    className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-400 hover:text-fuchsia-300 transition-colors"
                    title="Drop your override and use the auto-description"
                  >
                    ↺ reset to auto
                  </button>
                )}
              </div>
            </>
          ) : (
            <div
              className="text-xs text-zinc-200 leading-relaxed font-mono"
              data-testid={`scene-card-${idx}-anim-prompt-preview`}
              title="Click ✎ edit to customize"
            >
              {effectiveAnimPrompt || (
                <span className="text-zinc-500 italic">
                  (no description — Veo 3 will get a generic prompt)
                </span>
              )}
            </div>
          )}
        </div>

        {showEdit && (
          <div className="border border-zinc-800 rounded-sm bg-black/40 p-3 space-y-2">
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">
              CUSTOM PROMPT (optional — overrides the visual description)
            </div>
            <textarea
              data-testid={`scene-card-${idx}-prompt-input`}
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value.slice(0, 2000))}
              placeholder={description || "Describe the new image..."}
              rows={3}
              className="w-full bg-black border border-zinc-800 focus:border-yellow-400/60 focus:outline-none rounded-sm px-2.5 py-1.5 text-xs text-zinc-100 font-sans leading-relaxed resize-y"
            />
            <div className="flex items-center gap-2 justify-end">
              <button
                type="button"
                onClick={() => { setShowEdit(false); setCustomPrompt(""); }}
                className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-zinc-200 px-2 py-1.5"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid={`scene-card-${idx}-prompt-go`}
                onClick={handleRegen}
                disabled={regenBusy}
                className="font-mono text-[10px] uppercase tracking-[0.25em] bg-yellow-400 text-black hover:bg-yellow-300 disabled:bg-zinc-800 disabled:text-zinc-500 px-3 py-1.5 rounded-sm"
              >
                {regenBusy ? "regenerating…" : "↻ Regenerate"}
              </button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button
            type="button"
            data-testid={`scene-card-${idx}-approve`}
            onClick={handleApproveClick}
            disabled={status !== "ready" && status !== "approved" || approveBusy}
            className={`font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 rounded-sm transition-colors flex-1 sm:flex-none ${
              status === "approved"
                ? "bg-emerald-400/15 border border-emerald-400/50 text-emerald-300 hover:bg-emerald-400/20"
                : status === "ready"
                  ? "bg-emerald-400 text-black hover:bg-emerald-300"
                  : "bg-zinc-800 text-zinc-500 cursor-not-allowed"
            }`}
          >
            {approveBusy
              ? "…"
              : status === "approved"
                ? "✓ Approved (undo)"
                : "✓ Approve"}
          </button>
          <button
            type="button"
            data-testid={`scene-card-${idx}-regen-toggle`}
            onClick={() => setShowEdit((v) => !v)}
            disabled={regenBusy || status === "generating" || status === "pending"}
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-300 hover:text-yellow-400 border border-zinc-800 hover:border-yellow-400/60 disabled:text-zinc-600 disabled:hover:border-zinc-800 px-3 py-1.5 rounded-sm transition-colors"
          >
            {showEdit ? "× Close prompt" : "↻ Regenerate"}
          </button>
          <label
            className={`font-mono text-[10px] uppercase tracking-[0.25em] cursor-pointer text-zinc-300 hover:text-cyan-400 border border-zinc-800 hover:border-cyan-400/60 px-3 py-1.5 rounded-sm transition-colors ${
              uploadBusy ? "opacity-50 cursor-wait" : ""
            }`}
          >
            {uploadBusy ? "uploading…" : "⬆ Upload my own"}
            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              className="hidden"
              data-testid={`scene-card-${idx}-upload`}
              onChange={handleUpload}
              disabled={uploadBusy}
            />
          </label>
          <button
            type="button"
            data-testid={`scene-card-${idx}-animate`}
            onClick={onAnimate}
            disabled={!animateEnabled}
            className={`relative font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 rounded-sm transition-all border ${
              animatingNow
                ? "bg-fuchsia-500/20 border-fuchsia-400/70 text-fuchsia-200 cursor-wait"
                : animReady
                  ? "bg-fuchsia-500/10 border-fuchsia-400/50 text-fuchsia-300 hover:bg-fuchsia-500/20"
                  : animateEnabled
                    ? "bg-fuchsia-500 border-fuchsia-500 text-black hover:bg-fuchsia-400 font-bold shadow-[0_0_18px_rgba(217,70,239,0.35)]"
                    : "bg-zinc-900 border-zinc-800 border-dashed text-zinc-600 cursor-not-allowed"
            }`}
            title={
              animateEnabled
                ? animReady
                  ? "Re-animate this scene (replaces the 8s clip)"
                  : "Send this still to Veo 3 → 8-second animated clip"
                : animateDisabledReason
            }
            aria-label={
              animateEnabled
                ? `Animate scene ${idx + 1} with Veo 3`
                : `Animate disabled — ${animateDisabledReason}`
            }
          >
            {animatingNow ? (
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 border border-fuchsia-300 border-t-transparent rounded-full animate-spin" />
                Animating…
              </span>
            ) : animReady ? (
              "↻ Re-animate"
            ) : animFailed && isApproved ? (
              "↻ Retry animate"
            ) : animateEnabled ? (
              "▶ Animate"
            ) : (
              <span className="flex items-center gap-1.5">
                <span aria-hidden="true">🔒</span>
                Animate
              </span>
            )}
          </button>
        </div>

        {/* Inline help line when the button is locked — surfaces the
            reason directly under the actions row so the user doesn't
            have to hover to discover why nothing happens. */}
        {!animateEnabled && !animReady && animateDisabledReason && (
          <div
            data-testid={`scene-card-${idx}-animate-hint`}
            className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 -mt-1"
          >
            ▸ {animateDisabledReason}
          </div>
        )}

        {(animatingNow || animReady || animFailed) && (
          <div
            data-testid={`scene-card-${idx}-anim-block`}
            className="mt-2 border border-zinc-800 rounded-sm bg-black/40 p-3 space-y-2"
          >
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-fuchsia-300 flex items-center justify-between">
              <span>Veo3 · 8s animation</span>
              <span
                data-testid={`scene-card-${idx}-anim-status`}
                className={`px-1.5 py-0.5 rounded-sm border ${
                  animReady
                    ? "border-emerald-400/50 text-emerald-300"
                    : animFailed
                      ? "border-red-500/50 text-red-300"
                      : "border-fuchsia-400/50 text-fuchsia-300"
                }`}
              >
                {animReady
                  ? "ready"
                  : animFailed
                    ? "failed"
                    : "animating…"}
              </span>
            </div>
            {animatingNow && !animReady && (
              <div className="flex items-center gap-2 text-[11px] text-zinc-300">
                <div className="w-3 h-3 border-2 border-fuchsia-400/30 border-t-fuchsia-400 rounded-full animate-spin" />
                <span>Rendering 8-second clip via Google Veo 3…</span>
              </div>
            )}
            {animReady && (
              <video
                key={fullAnimUrl}
                src={fullAnimUrl}
                controls
                playsInline
                preload="metadata"
                data-testid={`scene-card-${idx}-anim-video`}
                className="w-full aspect-video bg-black rounded-sm"
              />
            )}
            {animFailed && (
              <div className="text-[11px] text-red-300 leading-relaxed">
                {animError || "Veo3 animation failed."}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
